import dayjs from 'dayjs';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { OFFICE_HOURS_FILTER_SUPPORT_DATE } from '../constants';
import { FilterValues } from './types';

export type OfficeHoursDateValidation = {
  isFullyBeforeSupportDate: boolean;
  isPartiallyBeforeSupportDate: boolean;
  isValid: boolean;
};

/**
 * Validates whether the current date range is compatible with Office Hours filtering. Checks the following scenarios:
 * - If the entire range is before `OFFICE_HOURS_FILTER_SUPPORT_DATE`
 * - If the range partially overlaps
 * - If the range is entirely on or after
 */
export const getOfficeHoursDateValidation = (startDate?: string, endDate?: string): OfficeHoursDateValidation => {
  if (!startDate || !endDate) {
    return { isFullyBeforeSupportDate: false, isPartiallyBeforeSupportDate: false, isValid: false };
  }

  const supportDate = dayjs(OFFICE_HOURS_FILTER_SUPPORT_DATE);
  const isStartBeforeSupport = dayjs(startDate).isBefore(supportDate);
  const isEndBeforeSupport = dayjs(endDate).isBefore(supportDate);

  const isFullyBeforeSupportDate = isStartBeforeSupport && isEndBeforeSupport;
  const isPartiallyBeforeSupportDate = isStartBeforeSupport && !isEndBeforeSupport;

  return {
    isFullyBeforeSupportDate,
    isPartiallyBeforeSupportDate,
    isValid: !isFullyBeforeSupportDate && !isPartiallyBeforeSupportDate,
  };
};

/**
 * Filter keys that depend on call intelligence analysis results.
 * These filters cannot be reliably matched against a call record that hasn't finished AI processing
 */
export const CALL_INTEL_ANALYSIS_FILTER_KEYS: (keyof FilterValues)[] = [
  'sentiments',
  'categories',
  'appointmentTypes',
  'aiOutcomes',
  'schedulingOutcomes',
  'schedulingOutcomeV2s',
  'serviceQualityFlags',
  'schedulingOpportunity',
  'hideNonAnalyzedCalls',
  'taskStatus',
];

export const hasCallIntelAnalysisFilters = (filters: CallIntelTypes.Filters & Partial<FilterValues>): boolean => {
  return CALL_INTEL_ANALYSIS_FILTER_KEYS.some((key) => {
    const value = filters[key as keyof typeof filters];
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.length > 0;
    return false;
  });
};
